import { useEffect, useState, useMemo } from 'react';
import Sidebar from '../../components/Layout/Sidebar';
import CashBookService from '../../services/CashBookService';

export default function CashBookView() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Fetch all cashbook records
  const loadData = async (from = null, to = null) => {
    setLoading(true);
    try {
      const res = await CashBookService.getCashBook();
      let data = res.transactions || [];

      // Filter by date if both from and to are selected
      if (from && to) {
        const fromTime = new Date(from).getTime();
        const toTime = new Date(to).getTime();
        data = data.filter((item) => {
          const itemTime = new Date(item.date).getTime();
          return itemTime >= fromTime && itemTime <= toTime;
        });
      }

      // Sort descending by id (latest first)
      data.sort((a, b) => b.id - a.id);
      setRecords(data);
    } catch (err) {
      console.error('Error loading cashbook:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Compute totals dynamically
  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    records.forEach((item) => {
      if (item.type === 'debit') debit += Number(item.amount);
      if (item.type === 'credit') credit += Number(item.amount);
    });
    const balance = credit - debit;
    return {
      debit: debit.toFixed(2),
      credit: credit.toFixed(2),
      balance: balance.toFixed(2),
    };
  }, [records]);

  // Compute running balance for each row (newest on top)
  const computeBalance = (index) => {
    let balance = 0;
    for (let i = records.length - 1; i >= index; i--) {
      const item = records[i];
      balance += item.type === 'credit' ? Number(item.amount) : -Number(item.amount);
    }
    return balance.toFixed(2);
  };

  // Handle date filter submit
  const handleFilter = (e) => {
    e.preventDefault();
    if (fromDate && toDate) {
      loadData(fromDate, toDate);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />

      <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
        <div className="max-w-7xl mx-auto">

          <h1 className="text-3xl font-bold text-white bg-black p-4 rounded mb-6">
            Cash Book
          </h1>

          {/* Date Filter Form */}
          <form
            onSubmit={handleFilter}
            className="bg-white rounded shadow p-4 mb-6 flex flex-wrap gap-4 items-end"
          >
            <div>
              <label className="block text-gray-700 font-medium">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border rounded p-2"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border rounded p-2"
              />
            </div>

            <button
              type="submit"
              className="bg-black text-white px-4 py-2 rounded hover:bg-gray-900"
            >
              Filter
            </button>

            <button
              type="button"
              onClick={() => {
                setFromDate('');
                setToDate('');
                loadData();
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Reset
            </button>
          </form>

          {/* Cashbook Table */}
          <div className="bg-white rounded shadow p-6 overflow-x-auto">

            {loading ? (
              <p className="text-center py-10 text-gray-500">
                Loading cash book...
              </p>
            ) : records.length === 0 ? (
              <p className="text-center py-10 text-gray-500">
                No transactions found.
              </p>
            ) : (
              <table className="min-w-full border text-sm">
                <thead>
                  <tr className="bg-gray-200 text-left">
                    <th className="p-2 border">Date</th>
                    <th className="p-2 border">Description</th>
                    <th className="p-2 border">Debit</th>
                    <th className="p-2 border">Credit</th>
                    <th className="p-2 border">Balance</th>
                  </tr>
                </thead>

                <tbody>
                  {records.map((item, index) => (
                    <tr key={item.id}>
                      <td className="p-2 border">{item.date}</td>
                      <td className="p-2 border">{item.description}</td>

                      <td className="p-2 border text-red-600 font-semibold text-right">
                        {item.type === 'debit' ? Number(item.amount).toFixed(2) : ''}
                      </td>

                      <td className="p-2 border text-green-600 font-semibold text-right">
                        {item.type === 'credit' ? Number(item.amount).toFixed(2) : ''}
                      </td>

                      <td className="p-2 border font-bold text-right">
                        {computeBalance(index)}
                      </td>
                    </tr>
                  ))}

                  {/* Totals Row */}
                  <tr className="bg-gray-100 font-bold">
                    <td className="p-2 border" colSpan="2">TOTAL</td>
                    <td className="p-2 border text-red-700 text-right">{totals.debit}</td>
                    <td className="p-2 border text-green-700 text-right">{totals.credit}</td>
                    <td className="p-2 border text-right">{totals.balance}</td>
                  </tr>
                </tbody>
              </table>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
